import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config/config';
import logger from '../utils/logger';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const USER_ROLES = {
  GUEST: 'guest',
  STARTER: 'starter',
  PRO: 'pro',
  UNLIMITED: 'unlimited',
  LIFETIME: 'lifetime',
  ADMIN: 'admin'
};

// Start with empty/safe defaults until the backend config loads
const defaultPlanLimits = {
  chatbots: 1,
  monthlyQueries: 500,
  monthlyResponses: 1000,
  fileUploads: 10,
  conversationHistoryDays: 7,
  leadGenForms: 1,
  urlTraining: 10,
  faqItems: 1,
  intentWorkflows: 0
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [canImpersonate, setCanImpersonate] = useState(false);
  const [userRole, setUserRole] = useState(USER_ROLES.GUEST);
  const [loading, setLoading] = useState(true);

  // Dynamic configurations from backend
  const [featuresList, setFeaturesList] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [planLimits, setPlanLimits] = useState({});
  const [userSpecificLimits, setUserSpecificLimits] = useState(null);
  const [userSpecificFeatures, setUserSpecificFeatures] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [usage, setUsage] = useState({
    chatbots: 0,
    monthlyQueries: 0,
    monthlyResponses: 0,
    fileUploads: 0
  });

  // Expose the customer identity to existing workspace code while binding Firebase
  // methods back to the real administrator. This keeps API tokens attributable to
  // the actor while browser-side ownership parameters follow the effective user.
  const effectiveUser = useMemo(() => {
    if (!user || !impersonation?.subject) return user;
    const subject = impersonation.subject;
    return new Proxy(user, {
      get(target, property) {
        if (property === 'uid') return subject.uid;
        if (property === 'email') return subject.email;
        if (property === 'displayName') return subject.name;
        const value = Reflect.get(target, property, target);
        return typeof value === 'function' ? value.bind(target) : value;
      }
    });
  }, [user, impersonation]);

  const getActorToken = useCallback(async () => {
    return auth.currentUser ? auth.currentUser.getIdToken() : null;
  }, []);

  const fetchImpersonationStatus = useCallback(async (firebaseUser) => {
    if (!firebaseUser) return { canImpersonate: false, impersonation: null };
    const token = await firebaseUser.getIdToken();
    const response = await fetch('/api/admin/impersonations/current', {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to load impersonation status');
    return response.json();
  }, []);

  // Fetch dynamic plans configuration on mount
  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        const response = await fetch('/api/config/plans');
        if (response.ok) {
          const data = await response.json();
          setFeaturesList(data.FEATURES);
          setRolePermissions(data.ROLE_PERMISSIONS);
          setPlanLimits(data.PLAN_LIMITS);
          setConfigLoaded(true);
        }
      } catch (err) {
        logger.error('Failed to load shared plan configurations:', err);
      }
    };
    fetchConfigs();
  }, []);

  // Fetch user usage from backend
  const fetchUserUsage = useCallback(async (userId) => {
    try {
      const token = await getActorToken();
      const response = await fetch(`/api/user/usage?userId=${userId}`, {
        credentials: 'include',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsage(data.usage || {
          chatbots: 0,
          monthlyQueries: 0,
          monthlyResponses: 0,
          fileUploads: 0
        });
      }
    } catch (error) {
      logger.error('Error fetching user usage:', error);
    }
  }, [getActorToken]);

  // Fetch subscription status and resolve role/overrides
  const fetchSubscriptionStatus = useCallback(async (currentUser) => {
    if (!currentUser) return;
    try {
      const email = currentUser.email?.toLowerCase();
      const isAdminAccount = email === 'asiffarhankhan@yahoo.com';

      if (isAdminAccount) {
        setUserRole(USER_ROLES.ADMIN);
      }

      const token = await currentUser.getIdToken();
      const response = await fetch('/api/subscription/status', {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      // Store user-specific limits and features if provided by the backend
      if (data.planInfo) {
        console.log('--- RBAC DEBUG ---');
        console.log('Backend Role:', data.planInfo.role);
        console.log('Backend Limits:', data.planInfo.limits);
        console.log('------------------');

        if (data.planInfo.role) setUserRole(data.planInfo.role);
        if (data.planInfo.limits) setUserSpecificLimits(data.planInfo.limits);
        if (data.planInfo.features) setUserSpecificFeatures(data.planInfo.features);
      } else if (data.subscription && data.subscription.status === 'active') {
        const planName = data.subscription.plan_name || '';
        if (planName.includes('Starter')) {
          setUserRole(USER_ROLES.STARTER);
        } else if (planName.includes('Pro')) {
          setUserRole(USER_ROLES.PRO);
        } else if (planName.includes('Unlimited')) {
          setUserRole(USER_ROLES.UNLIMITED);
        } else if (planName.includes('Lifetime')) {
          setUserRole(USER_ROLES.LIFETIME);
        } else if (planName.includes('Admin')) {
          setUserRole(USER_ROLES.ADMIN);
        } else {
          setUserRole(USER_ROLES.STARTER); // Default fallback
        }
      } else if (!isAdminAccount) {
        setUserRole(USER_ROLES.GUEST); // Or free tier if you have one
      }

      if (isAdminAccount) {
        setUserRole(USER_ROLES.ADMIN);
      }
    } catch (error) {
      logger.error('Error fetching subscription status:', error);
      if (currentUser?.email?.toLowerCase() === 'asiffarhankhan@yahoo.com') {
        setUserRole(USER_ROLES.ADMIN);
      } else {
        setUserRole(USER_ROLES.GUEST);
      }
    }
  }, []);

  // Increment usage for a specific type
  const incrementUsage = async (usageType, amount = 1) => {
    if (!effectiveUser) return;

    try {
      const token = await getActorToken();
      const response = await fetch('/api/user/usage/increment', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: effectiveUser.uid,
          usageType,
          amount
        })
      });

      if (response.ok) {
        // Refresh usage data
        await fetchUserUsage(effectiveUser.uid);
      }
    } catch (error) {
      logger.error('Error incrementing usage:', error);
    }
  };

  // Refresh user data
  const refreshUserData = useCallback(async () => {
    if (effectiveUser) {
      await fetchUserUsage(effectiveUser.uid);
      await fetchSubscriptionStatus(effectiveUser);
    }
  }, [effectiveUser, fetchUserUsage, fetchSubscriptionStatus]);

  // Update usage when user changes
  useEffect(() => {
    if (effectiveUser) {
      fetchUserUsage(effectiveUser.uid);
      fetchSubscriptionStatus(effectiveUser);
    }
  }, [effectiveUser, fetchUserUsage, fetchSubscriptionStatus]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        if (firebaseUser.email?.toLowerCase() === 'asiffarhankhan@yahoo.com') {
          setUserRole(USER_ROLES.ADMIN);
        }
        try {
          const status = await fetchImpersonationStatus(firebaseUser);
          setCanImpersonate(status.canImpersonate === true);
          setImpersonation(status.impersonation || null);
        } catch (error) {
          logger.error('Failed to initialize impersonation status:', error);
          setCanImpersonate(false);
          setImpersonation(null);
        }
        setUser(firebaseUser);
      } else {
        setUser(null);
        setCanImpersonate(false);
        setImpersonation(null);
        setUserRole(USER_ROLES.GUEST);
        setUserSpecificLimits(null);
        setUserSpecificFeatures(null);
        setUsage({
          chatbots: 0,
          monthlyQueries: 0,
          monthlyResponses: 0,
          fileUploads: 0
        });
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [fetchImpersonationStatus]);

  const startImpersonation = useCallback(async ({ identifier, reason, ticketId }) => {
    const token = await getActorToken();
    const response = await fetch('/api/admin/impersonations', {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ identifier, reason, ticketId })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to start impersonation');
    setImpersonation(data.impersonation);
    window.location.assign('/overview');
  }, [getActorToken]);

  const clearImpersonationSession = useCallback(async () => {
    const token = await getActorToken();
    const response = await fetch('/api/admin/impersonations/current', {
      method: 'DELETE',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok && response.status !== 204) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to stop impersonation');
    }
    setImpersonation(null);
  }, [getActorToken]);

  const stopImpersonation = useCallback(async () => {
    await clearImpersonationSession();
    window.location.assign('/overview');
  }, [clearImpersonationSession]);

  const signOutUser = useCallback(async () => {
    if (impersonation) await clearImpersonationSession();
    await auth.signOut();
  }, [impersonation, clearImpersonationSession]);

  // Log staging mode detection
  useEffect(() => {
    const isStaging = window.location.hostname === 'staging.smushlabs.ai';
    if (isStaging) {
      logger.log('🛠️ STAGING MODE: RBAC enforcement is ACTIVE for testing');
    }
  }, []);

  // Check if user has access to specific feature
  const hasFeatureAccess = (feature) => {
    if (userRole === USER_ROLES.ADMIN || effectiveUser?.email?.toLowerCase() === 'asiffarhankhan@yahoo.com') {
      return true;
    }
    if (!configLoaded) return true; // Safe fallback since Platform.js handles full loading blocks now

    // Defensive array check to prevent empty arrays from blocking everything
    const basePermissions = rolePermissions[userRole] || [];
    
    let permissions = basePermissions;
    if (userSpecificFeatures && Array.isArray(userSpecificFeatures) && userSpecificFeatures.length > 0) {
       permissions = userSpecificFeatures;
    }
    
    return permissions.includes(feature);
  };

  // Check if user has reached usage limit for a specific type
  const hasReachedLimit = (type) => {
    if (userRole === USER_ROLES.ADMIN || effectiveUser?.email?.toLowerCase() === 'asiffarhankhan@yahoo.com') {
      return false;
    }

    const limits = userSpecificLimits || planLimits[userRole] || planLimits[USER_ROLES.GUEST] || defaultPlanLimits;
    const limit = limits[type] !== undefined ? limits[type] : defaultPlanLimits[type];

    // -1 means unlimited
    if (limit === -1) return false;

    return (usage[type] || 0) >= limit;
  };

  // Get remaining usage for a specific type
  const getRemainingUsage = (type) => {
    if (userRole === USER_ROLES.ADMIN || effectiveUser?.email?.toLowerCase() === 'asiffarhankhan@yahoo.com') {
      return -1;
    }

    const limits = userSpecificLimits || planLimits[userRole] || planLimits[USER_ROLES.GUEST] || defaultPlanLimits;
    const limit = limits[type] !== undefined ? limits[type] : defaultPlanLimits[type];

    // -1 means unlimited
    if (limit === -1) return -1;

    return Math.max(0, limit - (usage[type] || 0));
  };

  // Get user's plan display name
  const getPlanDisplayName = () => {
    // Check if we're on staging domain - if so, add staging indicator
    const isStaging = window.location.hostname === 'staging.smushlabs.ai';

    let basePlanName;
    switch (userRole) {
      case USER_ROLES.GUEST:
        basePlanName = 'Guest';
        break;
      case USER_ROLES.STARTER:
        basePlanName = 'Starter';
        break;
      case USER_ROLES.PRO:
        basePlanName = 'Pro';
        break;
      case USER_ROLES.UNLIMITED:
        basePlanName = 'Unlimited';
        break;
      case USER_ROLES.LIFETIME:
        basePlanName = 'Lifetime';
        break;
      case USER_ROLES.ADMIN:
        basePlanName = 'Admin';
        break;
      default:
        basePlanName = 'Unknown';
    }

    return isStaging ? `${basePlanName} (Staging)` : basePlanName;
  };

  const value = {
    user: effectiveUser,
    actorUser: user,
    impersonation,
    canImpersonate,
    startImpersonation,
    stopImpersonation,
    signOutUser,
    userRole,
    usage,
    loading: loading || !configLoaded,
    hasFeatureAccess,
    hasReachedLimit,
    getRemainingUsage,
    getPlanDisplayName,
    refreshUserData,
    incrementUsage,
    planLimits: userSpecificLimits || planLimits[userRole] || planLimits[USER_ROLES.GUEST] || defaultPlanLimits,
    FEATURES: featuresList // Expose FEATURES enum for UI components to use
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
